import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import FirebaseLogin from '../components/FirebaseLogin';
import { useAuth } from '../context/AuthContext';
import { isFirebaseConfigured, cleanupFirebaseRedirectState } from '../config/firebase';
import { BRAND, Wordmark } from '../brand';
import { LandingNav } from './landing/LandingNav';
import { IS_IOS_APP } from '../config/platform';

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { loginWithFirebase } = useAuth();
  const [showFirebaseLogin, setShowFirebaseLogin] = useState(false);
  
  // Get returnTo from location state (for redirect after login)
  const returnTo = (location.state as { returnTo?: string })?.returnTo;

  useEffect(() => {
    // Clean up any stale Firebase redirect state to prevent "missing initial state" errors
    cleanupFirebaseRedirectState();
    
    // Clear any Firebase redirect-related URL parameters that might cause issues
    // These can occur if a user comes from an email link and Firebase tries to handle redirect state
    const searchParams = new URLSearchParams(location.search);
    const firebaseRedirectParams = ['apiKey', 'oobCode', 'mode', 'continueUrl', 'languageCode'];
    let hasFirebaseParams = false;
    firebaseRedirectParams.forEach(param => {
      if (searchParams.has(param)) {
        hasFirebaseParams = true;
        searchParams.delete(param);
      }
    });
    
    if (hasFirebaseParams) {
      // Update URL without Firebase redirect parameters
      const newSearch = searchParams.toString();
      const newPath = location.pathname + (newSearch ? `?${newSearch}` : '');
      window.history.replaceState({}, '', newPath);
    }
    
    // Check if Firebase is configured
    setShowFirebaseLogin(isFirebaseConfigured());
  }, [location.pathname, location.search]);

  const handleError = useCallback((error: Error | any) => {
    console.error('Login error:', error);
    // The error will be displayed by the FirebaseLogin component
    // This is just for logging
  }, []);

  const handleFirebaseSuccess = useCallback(async (idToken: string) => {
    try {
      await loginWithFirebase(idToken);
      // Redirect to returnTo if provided, otherwise go to dashboard
      navigate(returnTo || '/dashboard', { replace: true });
    } catch (error: any) {
      console.error('Firebase login error:', error);
      // If it's an email verification error, show it to the user
      if (error?.isEmailVerificationError || (error?.response?.status === 403 && error?.response?.data?.detail?.includes('Email verification'))) {
        const errorMessage = error?.message || error?.response?.data?.detail || 'Email verification required. Please verify your email address before accessing the app.';
        // Show error as alert for now - could be improved with a toast notification
        alert(errorMessage + '\n\nPlease check your inbox for a verification email and click the verification link.');
        handleError(error);
      } else {
        handleError(error);
      }
    }
  }, [loginWithFirebase, navigate, returnTo, handleError]);

  return (
    <div className="min-h-screen flex flex-col">
      {/* The landing nav is a marketing header — wordmark plus a "Get started" CTA.
          Inside an installed app there is no funnel left to run, and the CTA points
          at a signup flow the iOS build deliberately does not carry. */}
      {!IS_IOS_APP && <LandingNav />}
      <div className="flex-1 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8 relative z-10">
        {/* Logo and Header */}
        <div className="text-center">
          <div className="mx-auto h-16 w-16 rounded-2xl flex items-center justify-center shadow-lg transform hover:scale-105 transition-transform duration-200">
            <img src={BRAND.logoSrc} alt={BRAND.name} className="h-16 w-16" />
          </div>
          <h2 className="mt-6 text-3xl md:text-4xl font-extrabold text-gray-900 dark:text-gray-100">
            <Wordmark />
          </h2>
          <p className="mt-3 text-base md:text-lg text-gray-600 dark:text-gray-300">
            {BRAND.loginTagline}
          </p>
          <div className="mt-2 flex items-center justify-center space-x-4 text-sm text-gray-500 dark:text-gray-400">
            <span className="flex items-center">
              <svg className="w-4 h-4 mr-1 text-green-500 dark:text-green-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
              </svg>
              Fast
            </span>
            <span className="flex items-center">
              <svg className="w-4 h-4 mr-1 text-green-500 dark:text-green-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              Easy
            </span>
          </div>
        </div>

        {/* Login Card */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-6 md:p-8 space-y-6 transform hover:shadow-3xl transition-shadow duration-300">
          <div className="space-y-4">
            <h3 className="text-lg md:text-xl font-semibold text-gray-900 dark:text-gray-100 text-center">
              Sign in to your account
            </h3>

            {/* Firebase Email/Password Login */}
            {showFirebaseLogin && (
              <div className="transform transition-all duration-200">
                <FirebaseLogin onSuccess={handleFirebaseSuccess} onError={handleError} />
              </div>
            )}
          </div>

        </div>

        {/* Footer */}
        <div className="text-center text-sm text-gray-500 dark:text-gray-400 space-y-2">
          <p className="text-xs">
            By signing in, you agree to our Terms of Service and Privacy Policy
          </p>
        </div>
      </div>
      </div>
    </div>
  );
};

export default LoginPage;
